'use client';

import { cn } from '../utils/cn';

export interface PaginationProps {
  /** Current 1-based page index */
  page: number;
  /** Items per page (default 25) */
  pageSize: number;
  /** Total number of items across all pages */
  total: number;
  /** Number of items rendered on the current page (used in the "نمایش X از Y" label) */
  displayed?: number;
  onPageChange: (page: number) => void;
  /** Optional rows-per-page selector. When omitted the selector is hidden. */
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
  /** Persian or English labels — defaults to Persian for the website. */
  labels?: {
    prev?: string;
    next?: string;
    of?: string;
    perPage?: string;
    showingPrefix?: string;
    items?: string;
  };
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

function toFaDigits(value: number | string): string {
  return String(value).replace(/\d/g, (digit) => FA_DIGITS[Number(digit)]);
}

/**
 * Shared customer-facing pagination control. Used across the website
 * dashboard tables (orders, wallet, support tickets) for a consistent
 * "25 per page" experience.
 */
export function Pagination({
  page,
  pageSize,
  total,
  displayed,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  className,
  labels,
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, pageSize)));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startItem = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = total === 0 ? 0 : Math.min(total, startItem + (displayed ?? pageSize) - 1);

  const goTo = (next: number) => {
    if (next < 1 || next > totalPages || next === safePage) return;
    onPageChange(next);
  };

  const buildPages = (): Array<number | 'ellipsis-left' | 'ellipsis-right'> => {
    const pages: Array<number | 'ellipsis-left' | 'ellipsis-right'> = [];
    const window = 1;
    const start = Math.max(2, safePage - window);
    const end = Math.min(totalPages - 1, safePage + window);
    pages.push(1);
    if (start > 2) pages.push('ellipsis-left');
    for (let p = start; p <= end; p += 1) pages.push(p);
    if (end < totalPages - 1) pages.push('ellipsis-right');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  const pages = buildPages();
  const L = {
    prev: 'قبلی',
    next: 'بعدی',
    of: 'از',
    perPage: 'تعداد در صفحه:',
    showingPrefix: 'نمایش',
    items: 'مورد',
    ...labels,
  };

  if (total === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-between px-4 py-3 text-sm text-gray-500',
          className,
        )}
      >
        <span>
          {L.showingPrefix} {toFaDigits(0)} {L.of} {toFaDigits(0)} {L.items}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
        <span>
          {L.showingPrefix} {toFaDigits(startItem)}–{toFaDigits(endItem)} {L.of}{' '}
          {toFaDigits(total)} {L.items}
        </span>
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{L.perPage}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (!Number.isNaN(next) && next > 0) {
                  onPageSizeChange(next);
                }
              }}
              className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {toFaDigits(option)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => goTo(safePage - 1)}
          disabled={safePage === 1}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {L.prev}
        </button>

        <div className="flex items-center gap-1">
          {pages.map((entry, index) => {
            if (entry === 'ellipsis-left' || entry === 'ellipsis-right') {
              return (
                <span key={`${entry}-${index}`} className="px-2 text-gray-400">
                  …
                </span>
              );
            }
            const isActive = entry === safePage;
            return (
              <button
                key={entry}
                type="button"
                onClick={() => goTo(entry)}
                className={cn(
                  'min-w-[2rem] rounded-lg border px-2 py-1 text-sm transition-colors',
                  isActive
                    ? 'border-amber-500 bg-amber-500 text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                )}
              >
                {toFaDigits(entry)}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => goTo(safePage + 1)}
          disabled={safePage === totalPages}
          className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {L.next}
        </button>
      </div>
    </div>
  );
}
